/**
 * Image Comparison Slider
 * Enables before/after image comparison with draggable slider
 */
(function() {
  'use strict';

  const SELECTORS = {
    container: '.image-comparison-container',
    overlay: '.image-comparison-overlay',
    slider: '.image-comparison-slider'
  };

  class ImageComparison {
    constructor(container) {
      this.container = container;
      this.overlay = container.querySelector(SELECTORS.overlay);
      this.slider = null;
      this.isDragging = false;
      this.width = 0;
      
      if (!this.overlay) {
        console.warn('ImageComparison: Overlay element not found');
        return;
      }

      this.init();
    }

    init() {
      // Wait for images to load before initializing
      const images = this.container.querySelectorAll('img');
      const imagesToLoad = Array.from(images).filter(img => !img.complete);
      
      if (imagesToLoad.length > 0) {
        Promise.all(imagesToLoad.map(img => {
          return new Promise(resolve => {
            if (img.complete) {
              resolve();
            } else {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            }
          });
        })).then(() => this.createSlider());
      } else {
        this.createSlider();
      }
    }

    createSlider() {
      // Remove existing slider if present
      const existingSlider = this.container.querySelector(SELECTORS.slider);
      if (existingSlider) {
        existingSlider.remove();
      }

      this.width = this.container.offsetWidth;
      const initialWidth = this.width / 2;
      
      // Set initial overlay width
      this.overlay.style.width = initialWidth + 'px';
      
      // Create slider element
      this.slider = document.createElement('div');
      this.slider.className = 'image-comparison-slider';
      this.container.appendChild(this.slider);
      
      // Position slider
      this.updateSliderPosition(initialWidth);
      
      // Bind events
      this.bindEvents();
    }

    bindEvents() {
      this.slider.addEventListener('mousedown', this.handleStart.bind(this));
      this.slider.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });
      
      // Use event delegation for move/end events to handle mouse leaving container
      document.addEventListener('mousemove', this.handleMove.bind(this));
      document.addEventListener('mouseup', this.handleEnd.bind(this));
      document.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
      document.addEventListener('touchend', this.handleEnd.bind(this));
    }

    handleStart(e) {
      e.preventDefault();
      this.isDragging = true;
      this.container.classList.add('is-dragging');
    }

    handleMove(e) {
      if (!this.isDragging) return;
      
      e.preventDefault();
      const pos = this.getCursorPosition(e);
      const clampedPos = Math.max(0, Math.min(pos, this.width));
      this.updateSliderPosition(clampedPos);
    }

    handleEnd() {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      this.container.classList.remove('is-dragging');
    }

    getCursorPosition(e) {
      const touch = e.touches ? e.touches[0] : e.changedTouches ? e.changedTouches[0] : null;
      const clientX = touch ? touch.clientX : e.clientX;
      const rect = this.container.getBoundingClientRect();
      return clientX - rect.left;
    }

    updateSliderPosition(x) {
      this.overlay.style.width = x + 'px';
      if (this.slider) {
        // Position slider at the edge of overlay (slider is 4px wide, so center it)
        this.slider.style.left = (x - 2) + 'px';
      }
    }

    destroy() {
      if (this.slider) {
        this.slider.remove();
        this.slider = null;
      }
      this.isDragging = false;
    }

    // Recalculate on resize
    recalculate() {
      const newWidth = this.container.offsetWidth;
      if (newWidth !== this.width) {
        this.width = newWidth;
        const currentWidth = parseFloat(this.overlay.style.width) || this.width / 2;
        const ratio = currentWidth / this.width;
        this.updateSliderPosition(this.width * ratio);
      }
    }
  }

  // Initialize all image comparisons on page load
  function initImageComparisons() {
    const containers = document.querySelectorAll(SELECTORS.container);
    const instances = [];
    
    containers.forEach(container => {
      // Skip if already initialized
      if (container.dataset.comparisonInitialized) return;
      
      const instance = new ImageComparison(container);
      if (instance.slider) {
        container.dataset.comparisonInitialized = 'true';
        instances.push(instance);
      }
    });

    return instances;
  }

  // Debounce utility (reuse theme's debounce if available)
  function debounce(func, wait) {
    if (window.debounce) {
      return window.debounce(func, wait);
    }
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Store instances for resize handling
  let comparisonInstances = [];

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      comparisonInstances = initImageComparisons();
    });
  } else {
    comparisonInstances = initImageComparisons();
  }

  // Handle resize with debounce
  const handleResize = debounce(() => {
    comparisonInstances.forEach(instance => {
      if (instance && typeof instance.recalculate === 'function') {
        instance.recalculate();
      }
    });
  }, 250);

  window.addEventListener('resize', handleResize);

  // Re-initialize on Shopify theme editor changes
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      comparisonInstances = initImageComparisons();
    });
  }

  // Export for manual initialization if needed
  window.ImageComparison = ImageComparison;
  window.initImageComparisons = initImageComparisons;
})();

